import React, { useState, useMemo } from 'react';
import { Property } from '../types/property.types';
import { useAuth } from '../contexts/AuthContext';
import { useProperties } from '../hooks/useProperties';
import { formatters } from '../services/propertyService';
import { DateFilters } from './DateFilters';
import SmartPropertyCodes from './SmartPropertyCodes';
import PropertySummary from './PropertySummary';
import PlatformUrls from './PlatformUrls';

interface PropertyListProps {
  selectedProperty?: Property | null;
  onPropertySelect?: (property: Property) => void;
  onPropertyEdit?: (property: Property | null) => void;
}

const PropertyList: React.FC<PropertyListProps> = ({
  selectedProperty,
  onPropertySelect,
  onPropertyEdit
}) => {
  const { user, canEdit, canViewOwnerData } = useAuth();
  const { properties, loading, error, loadProperties, deleteProperty } = useProperties();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterTransaction, setFilterTransaction] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSede, setFilterSede] = useState('');
  const [dateFilters, setDateFilters] = useState<any>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(new Set());
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Función para filtrar por fechas
  const filterByDate = (property: Property, filters: any) => {
    if (!filters || Object.keys(filters).length === 0) return true;

    const propertyDate = new Date(property.fecha_creacion || property.ultima_actualizacion || '2025-01-01');
    const propertyDateStr = propertyDate.toISOString().split('T')[0];

    // Filtro por fecha de creación
    if (filters.fecha_creacion_inicio) {
      if (propertyDateStr < filters.fecha_creacion_inicio) return false;
    }
    
    if (filters.fecha_creacion_fin) {
      if (propertyDateStr > filters.fecha_creacion_fin) return false;
    }

    // Filtro por mes de captación
    if (filters.mes_captacion) {
      const mesMatch = property.mes_captacion?.toLowerCase().includes(filters.mes_captacion.toLowerCase());
      if (!mesMatch) return false;
    }

    // Filtro por propiedades modificadas recientemente
    if (filters.solo_modificadas) {
      const isModified = property.ultima_actualizacion && property.ultima_actualizacion.includes('MODIFICADO');
      if (!isModified) return false;
    }

    return true;
  };

  // Función para manejar cambios en filtros de fecha
  const handleDateFiltersChange = (filters: any) => {
    setDateFilters(filters);
    setCurrentPage(1); // Reset página cuando cambian los filtros
  };

  const handleDeleteProperty = async (property: Property) => {
    if (!canEdit) return;
    if (window.confirm(`¿Está seguro de eliminar la propiedad ${property.codigo_inmueble}?`)) {
      try {
        await deleteProperty(property.id);
      } catch (error) {
        console.error('Error deleting property:', error);
      }
    }
  };

  const handleEditProperty = (property: Property) => {
    if (!canEdit) return;
    onPropertyEdit?.(property);
  };

  const filteredProperties = useMemo(() => {
    let filtered = properties;
    
    // Filtro por texto - buscar en múltiples campos incluyendo códigos de plataformas
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.codigo_inmueble?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.prointegral?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.metro_cuadrado?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.espacio_urbano?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.finca_raiz?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.cien_cuadras?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.proppit?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.direccion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.ciudad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sector?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.nombre_propietario?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.edificio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.asesor?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filtro por tipo
    if (filterType) {
      filtered = filtered.filter(p => p.tipo_inmueble === filterType);
    }
    
    // Filtro por transacción
    if (filterTransaction) {
      filtered = filtered.filter(p => p.transaccion === filterTransaction);
    }

    // Filtro por estado
    if (filterStatus) {
      filtered = filtered.filter(p => p.estado === filterStatus);
    }
    
    // Filtro por sede
    if (filterSede) {
      filtered = filtered.filter(p => p.sede === filterSede);
    }

    // Filtro por fechas
    filtered = filtered.filter(p => filterByDate(p, dateFilters));
    
    // Reset página cuando cambian los filtros
    setCurrentPage(1);
    
    return filtered;
  }, [properties, searchTerm, filterType, filterTransaction, filterStatus, dateFilters]);

  // Calcular propiedades a mostrar en la página current
  const paginatedProperties = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProperties.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredProperties, currentPage, itemsPerPage]);

  // Estadísticas rápidas de los resultados filtrados
  const quickStats = useMemo(() => {
    const stats = {
      total: filteredProperties.length,
      disponibles: filteredProperties.filter(p => p.estado === 'Disponible').length,
      arriendo: filteredProperties.filter(p => p.transaccion === 'Arriendo' || p.transaccion === 'Ambos').length,
      venta: filteredProperties.filter(p => p.transaccion === 'Venta' || p.transaccion === 'Ambos').length,
      promedioArriendo: 0,
      promedioVenta: 0
    };

    const arriendos = filteredProperties.filter(p => p.valor_arriendo && p.valor_arriendo > 0);
    if (arriendos.length > 0) {
      stats.promedioArriendo = Math.round(arriendos.reduce((sum, p) => sum + (p.valor_arriendo || 0), 0) / arriendos.length);
    }

    const ventas = filteredProperties.filter(p => p.valor_venta && p.valor_venta > 0);
    if (ventas.length > 0) {
      stats.promedioVenta = Math.round(ventas.reduce((sum, p) => sum + (p.valor_venta || 0), 0) / ventas.length);
    }

    return stats;
  }, [filteredProperties]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
        <button 
          onClick={() => loadProperties()}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con estadísticas */}
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-900">
            Propiedades {user?.role !== 'admin' && `(${user?.role})`}
          </h2>
          
          {/* Estadísticas rápidas */}
          <div className="flex space-x-2">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              📊 {quickStats.total} total
            </span>
            {quickStats.disponibles > 0 && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                ✅ {quickStats.disponibles} disponibles
              </span>
            )}
            {quickStats.arriendo > 0 && (
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                🏠 {quickStats.arriendo} arriendo
              </span>
            )}
            {quickStats.venta > 0 && (
              <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                💰 {quickStats.venta} venta
              </span>
            )}
          </div>
          
          {dateFilters && Object.keys(dateFilters).length > 0 && (
            <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
              🗓️ Filtro temporal activo
            </span>
          )}
        </div>
        
        {canEdit && (
          <button
            onClick={() => onPropertyEdit?.(null)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Nueva Propiedad</span>
          </button>
        )}
      </div>

      {/* Filtros de fecha */}
      <DateFilters 
        onFiltersChange={handleDateFiltersChange}
        loading={loading}
        propertiesCount={filteredProperties.length}
        totalProperties={properties.length}
      />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar propiedades
            </label>
            <input
              type="text"
              placeholder="Buscar por código (Prointegral, Metro², Espacio Urbano, Finca Raíz), dirección, ciudad, asesor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de inmueble
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los tipos</option>
              <option value="Apto">Apartamento</option>
              <option value="Casa">Casa</option>
              <option value="Local">Local</option>
              <option value="Oficina">Oficina</option>
              <option value="Consultorio">Consultorio</option>
              <option value="Bodega">Bodega</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transacción
            </label>
            <select
              value={filterTransaction}
              onChange={(e) => setFilterTransaction(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas las transacciones</option>
              <option value="Arriendo">Arriendo</option>
              <option value="Venta">Venta</option>
              <option value="Ambos">Ambos</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value="Disponible">Disponible</option>
              <option value="Arrendado">Arrendado</option>
              <option value="Vendido">Vendido</option>
              <option value="No Disponible">No Disponible</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sede
            </label>
            <select
              value={filterSede}
              onChange={(e) => setFilterSede(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas las sedes</option>
              {Array.from(new Set(properties.map(p => p.sede).filter(Boolean))).map(sede => (
                <option key={sede} value={sede}>{sede}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Acciones
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('');
                  setFilterTransaction('');
                  setFilterStatus('');
                  setFilterSede('');
                  setDateFilters({});
                }}
                className="flex-1 px-3 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors flex items-center justify-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Limpiar</span>
              </button>
              
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="px-3 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                title={viewMode === 'grid' ? 'Vista de lista' : 'Vista de tarjetas'}
              >
                {viewMode === 'grid' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Indicadores de filtros activos */}
        {(searchTerm || filterType || filterTransaction || filterStatus || Object.keys(dateFilters).length > 0) && (
          <div className="pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Filtros activos:
              </span>
              <span className="text-sm text-gray-500">
                {filteredProperties.length} de {properties.length} propiedades
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {searchTerm && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Búsqueda: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {filterType && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Tipo: {filterType}
                  <button
                    onClick={() => setFilterType('')}
                    className="ml-1 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {filterTransaction && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Transacción: {filterTransaction}
                  <button
                    onClick={() => setFilterTransaction('')}
                    className="ml-1 text-purple-600 hover:text-purple-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {filterStatus && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Estado: {filterStatus}
                  <button
                    onClick={() => setFilterStatus('')}
                    className="ml-1 text-orange-600 hover:text-orange-800"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Properties Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedProperties.map((property) => (
            <div
              key={property.id}
              className={`bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                selectedProperty?.id === property.id ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => onPropertySelect?.(property)}
            >
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <SmartPropertyCodes property={property} compact={true} />
                    {property.ultima_actualizacion?.includes('MODIFICADO') && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        Modificado
                      </span>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    property.estado === 'Disponible' ? 'bg-green-100 text-green-800' :
                    property.estado === 'Arrendado' ? 'bg-yellow-100 text-yellow-800' :
                    property.estado === 'Vendido' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {property.estado}
                  </span>
                </div>
                
                <h3 className="font-semibold text-gray-900 mb-1 flex items-center justify-between">
                  <span>{property.tipo_inmueble} - {property.transaccion}</span>
                  {property.asesor && (
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                      👤 {property.asesor}
                    </span>
                  )}
                </h3>
                
                <p className="text-sm text-gray-600 mb-2">
                  {property.direccion}
                </p>
                
                <p className="text-sm text-gray-500 mb-3">
                  {property.sector}, {property.ciudad}
                </p>
                
                <div className="text-lg font-bold text-green-600 mb-3">
                  {formatters.propertyPrice(property)}
                </div>
                
                {/* Información detallada del inmueble */}
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                  {(property.habitaciones ?? 0) > 0 && (
                    <div className="flex items-center space-x-1">
                      <span>🛏️</span>
                      <span>{property.habitaciones} habitaciones</span>
                    </div>
                  )}
                  {(property.banos ?? 0) > 0 && (
                    <div className="flex items-center space-x-1">
                      <span>🚿</span>
                      <span>{property.banos} baños</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1">
                    <span>🚗</span>
                    <span>{property.parqueaderos} parqueaderos</span>
                  </div>
                  {property.estrato && (
                    <div className="flex items-center space-x-1">
                      <span>📊</span>
                      <span>Estrato {property.estrato}</span>
                    </div>
                  )}
                  {property.area_construida && (
                    <div className="flex items-center space-x-1">
                      <span>📐</span>
                      <span>{property.area_construida}m² construidos</span>
                    </div>
                  )}
                  {property.area_total && (
                    <div className="flex items-center space-x-1">
                      <span>📏</span>
                      <span>{property.area_total}m² totales</span>
                    </div>
                  )}
                  {property.administracion && (
                    <div className="flex items-center space-x-1">
                      <span>💰</span>
                      <span>Admin: ${property.administracion.toLocaleString()}</span>
                    </div>
                  )}
                  {property.amoblado && property.amoblado !== 'Vacio' && (
                    <div className="flex items-center space-x-1">
                      <span>🪑</span>
                      <span>{property.amoblado}</span>
                    </div>
                  )}
                  {property.piscina && (
                    <div className="flex items-center space-x-1">
                      <span>🏊‍♂️</span>
                      <span>Piscina</span>
                    </div>
                  )}
                  {property.ascensor && (
                    <div className="flex items-center space-x-1">
                      <span>🛗</span>
                      <span>Ascensor</span>
                    </div>
                  )}
                  {property.balcon && (
                    <div className="flex items-center space-x-1">
                      <span>🌿</span>
                      <span>Balcón</span>
                    </div>
                  )}
                  {property.niveles && property.niveles > 1 && (
                    <div className="flex items-center space-x-1">
                      <span>🏢</span>
                      <span>{property.niveles} niveles</span>
                    </div>
                  )}
                </div>

                {/* Información temporal */}
                <div className="text-xs text-gray-400 mb-3 space-y-1">
                  {property.sede && (
                    <div className="flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd" />
                      </svg>
                      Sede: {property.sede}
                    </div>
                  )}
                  {property.mes_captacion && (
                    <div className="flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      Captado: {property.mes_captacion}
                    </div>
                  )}
                  {property.fecha_creacion && (
                    <div className="flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      Creado: {new Date(property.fecha_creacion).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Códigos de todas las plataformas */}
                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <SmartPropertyCodes property={property} />
                </div>

                {canViewOwnerData && (property.nombre_propietario || property.telefono_propietario) && (
                  <div className="border-t pt-3 mt-3">
                    <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                      👤 Información del Propietario
                    </div>
                    {property.nombre_propietario && (
                      <p className="text-sm font-medium text-gray-900">{property.nombre_propietario}</p>
                    )}
                    {property.telefono_propietario && (
                      <p className="text-sm text-gray-600">📞 {property.telefono_propietario}</p>
                    )}
                    {property.email_propietario && (
                      <p className="text-sm text-gray-600">📧 {property.email_propietario}</p>
                    )}
                  </div>
                )}

                {/* Información adicional */}
                {(property.servicios || property.zonas_comunes || property.llaves || property.observaciones) && (
                  <div className="border-t pt-3 mt-3">
                    <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                      ℹ️ Información Adicional
                    </div>
                    <div className="space-y-2 text-sm">
                      {property.servicios && (
                        <div>
                          <span className="font-medium text-gray-700">Servicios:</span>
                          <span className="text-gray-600 ml-1">{property.servicios}</span>
                        </div>
                      )}
                      {property.zonas_comunes && (
                        <div>
                          <span className="font-medium text-gray-700">Zonas comunes:</span>
                          <span className="text-gray-600 ml-1">{property.zonas_comunes}</span>
                        </div>
                      )}
                      {property.llaves && (
                        <div>
                          <span className="font-medium text-gray-700">Llaves:</span>
                          <span className="text-gray-600 ml-1">{property.llaves}</span>
                        </div>
                      )}
                      {property.administracion && (
                        <div>
                          <span className="font-medium text-gray-700">Administración:</span>
                          <span className="text-gray-600 ml-1">${property.administracion.toLocaleString()}</span>
                        </div>
                      )}
                      {property.predial && (
                        <div>
                          <span className="font-medium text-gray-700">Predial:</span>
                          <span className="text-gray-600 ml-1">${property.predial.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {property.observaciones && (
                  <div className="border-t pt-3 mt-3">
                    <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                      📝 Observaciones
                    </div>
                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      {property.observaciones}
                    </p>
                  </div>
                )}

                <div className="flex justify-between items-center mt-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPropertySelect?.(property);
                      }}
                      className="px-3 py-1 bg-blue-100 text-blue-800 hover:bg-blue-200 rounded text-sm transition-colors flex items-center space-x-1"
                    >
                      <span>🗺️</span>
                      <span>Ver en mapa</span>
                    </button>
                    
                    {property.telefono_propietario && canViewOwnerData && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const message = `Hola, me interesa la propiedad ${property.tipo_inmueble} en ${property.direccion}, ${property.sector}. ¿Podemos coordinar una cita?`;
                          const phone = property.telefono_propietario?.replace(/\D/g, '');
                          if (phone) {
                            window.open(`https://wa.me/57${phone}?text=${encodeURIComponent(message)}`);
                          }
                        }}
                        className="px-3 py-1 bg-green-100 text-green-800 hover:bg-green-200 rounded text-sm transition-colors flex items-center space-x-1"
                      >
                        <span>💬</span>
                        <span>WhatsApp</span>
                      </button>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const isExpanded = expandedProperties.has(property.id);
                        const newExpanded = new Set(expandedProperties);
                        if (isExpanded) {
                          newExpanded.delete(property.id);
                        } else {
                          newExpanded.add(property.id);
                        }
                        setExpandedProperties(newExpanded);
                      }}
                      className="px-3 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded text-sm transition-colors flex items-center space-x-1"
                    >
                      <span>{expandedProperties.has(property.id) ? '👆' : '👇'}</span>
                      <span>{expandedProperties.has(property.id) ? 'Menos' : 'Más detalles'}</span>
                    </button>
                  </div>

                  <div className="flex space-x-1">
                    {canViewOwnerData && (
                      <>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (property.email_propietario) {
                              window.open(`mailto:${property.email_propietario}?subject=Consulta sobre propiedad ${property.codigo_inmueble}&body=Hola, me interesa conocer más información sobre la propiedad ubicada en ${property.direccion}.`);
                            }
                          }}
                          className={`p-2 rounded-full ${property.email_propietario ? 'text-gray-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'} transition-colors`}
                          title={property.email_propietario ? `Enviar email a ${property.email_propietario}` : 'No hay email disponible'}
                        >
                          📧
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (property.telefono_propietario) {
                              // Limpiar número y usar tel:
                              const cleanPhone = property.telefono_propietario.replace(/\D/g, '');
                              if (cleanPhone) {
                                console.log('Llamando a:', cleanPhone);
                                window.location.href = `tel:+57${cleanPhone}`;
                              } else {
                                alert('Número de teléfono no válido');
                              }
                            } else {
                              alert('No hay teléfono disponible');
                            }
                          }}
                          className={`p-2 rounded-full ${property.telefono_propietario ? 'text-gray-400 hover:text-green-600 hover:bg-green-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'} transition-colors`}
                          title={property.telefono_propietario ? `Llamar a ${property.telefono_propietario}` : 'No hay teléfono disponible'}
                        >
                          📞
                        </button>
                      </>
                    )}

                    {canEdit && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Editando propiedad:', property.codigo_inmueble);
                            handleEditProperty(property);
                          }}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                          title="Editar propiedad"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Eliminando propiedad:', property.codigo_inmueble);
                            if (confirm(`¿Estás seguro de eliminar la propiedad ${property.codigo_inmueble}?`)) {
                              handleDeleteProperty(property);
                            }
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title="Eliminar propiedad"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Sección expandible con información completa */}
                {expandedProperties.has(property.id) && (
                  <div className="mt-4 pt-4 border-t bg-gray-50 -mx-4 px-4 pb-4">
                    <PropertySummary property={property} showOwnerData={canViewOwnerData} />
                    
                    {/* 🚀 NUEVO: URLs de plataformas inmobiliarias */}
                    <div className="mt-4">
                      <PlatformUrls property={property} compact={true} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Códigos/Propiedad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ubicación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Características
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fechas
                  </th>
                  {canViewOwnerData && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Propietario
                    </th>
                  )}
                  {canEdit && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedProperties.map((property) => (
                  <React.Fragment key={property.id}>
                    <tr 
                      className={`hover:bg-gray-50 cursor-pointer ${
                        selectedProperty?.id === property.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => onPropertySelect?.(property)}
                    >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <SmartPropertyCodes property={property} />
                        {property.ultima_actualizacion?.includes('MODIFICADO') && (
                          <span className="mt-1 inline-block px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                            Modificado
                          </span>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          {property.tipo_inmueble} - {property.transaccion}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{property.direccion}</div>
                      <div className="text-sm text-gray-500">{property.sector}, {property.ciudad}</div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-600">
                        {formatters.propertyPrice(property)}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-3">
                          {(property.habitaciones ?? 0) > 0 && <span>🛏️ {property.habitaciones}</span>}
                          {(property.banos ?? 0) > 0 && <span>🚿 {property.banos}</span>}
                          <span>🚗 {property.parqueaderos}</span>
                        </div>
                        <div className="flex items-center space-x-3 text-xs">
                          {property.estrato && <span>� E{property.estrato}</span>}
                          {property.area_construida && <span>📐 {property.area_construida}m²</span>}
                        </div>
                        {(property.piscina || property.ascensor || property.balcon) && (
                          <div className="flex space-x-1 text-xs">
                            {property.piscina && <span title="Piscina">🏊‍♂️</span>}
                            {property.ascensor && <span title="Ascensor">🛗</span>}
                            {property.balcon && <span title="Balcón">🌿</span>}
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        property.estado === 'Disponible' ? 'bg-green-100 text-green-800' :
                        property.estado === 'Arrendado' ? 'bg-yellow-100 text-yellow-800' :
                        property.estado === 'Vendido' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {property.estado}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        {property.mes_captacion && (
                          <div className="text-xs">📅 {property.mes_captacion}</div>
                        )}
                        {property.fecha_creacion && (
                          <div className="text-xs">⏰ {new Date(property.fecha_creacion).toLocaleDateString()}</div>
                        )}
                      </div>
                    </td>

                    {canViewOwnerData && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {property.nombre_propietario && (
                          <div className="font-medium text-gray-900">{property.nombre_propietario}</div>
                        )}
                        {property.telefono_propietario && (
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">{property.telefono_propietario}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const message = `Hola, me interesa la propiedad ${property.tipo_inmueble} en ${property.direccion}`;
                                const phone = property.telefono_propietario?.replace(/\D/g, '');
                                if (phone) {
                                  window.open(`https://wa.me/57${phone}?text=${encodeURIComponent(message)}`);
                                }
                              }}
                              className="text-green-600 hover:text-green-800 text-xs"
                              title="Enviar WhatsApp"
                            >
                              💬
                            </button>
                          </div>
                        )}
                        {property.email_propietario && (
                          <div className="text-gray-500 text-xs">{property.email_propietario}</div>
                        )}
                      </td>
                    )}

                    {canEdit && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onPropertySelect?.(property);
                            }}
                            className="px-2 py-1 bg-blue-100 text-blue-800 hover:bg-blue-200 rounded text-xs transition-colors flex items-center space-x-1"
                            title="Ver en mapa"
                          >
                            <span>🗺️</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const isExpanded = expandedProperties.has(property.id);
                              const newExpanded = new Set(expandedProperties);
                              if (isExpanded) {
                                newExpanded.delete(property.id);
                              } else {
                                newExpanded.add(property.id);
                              }
                              setExpandedProperties(newExpanded);
                            }}
                            className="px-2 py-1 bg-purple-100 text-purple-800 hover:bg-purple-200 rounded text-xs transition-colors flex items-center space-x-1"
                            title="Ver enlaces de plataformas"
                          >
                            <span>🔗</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProperty(property);
                            }}
                            className="px-2 py-1 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded text-xs transition-colors flex items-center space-x-1"
                            title="Editar propiedad"
                          >
                            <span>✏️</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProperty(property);
                            }}
                            className="px-2 py-1 bg-red-100 text-red-800 hover:bg-red-200 rounded text-xs transition-colors flex items-center space-x-1"
                            title="Eliminar propiedad"
                          >
                            <span>🗑️</span>
                          </button>
                        </div>
                      </td>
                    )}
                    </tr>
                    
                    {/* 🚀 NUEVA FILA EXPANDIBLE: URLs de plataformas */}
                    {expandedProperties.has(property.id) && (
                      <tr>
                        <td colSpan={canViewOwnerData && canEdit ? 8 : canViewOwnerData || canEdit ? 7 : 6} 
                            className="px-6 py-4 bg-gray-50 border-t">
                          <div className="max-w-4xl">
                            <PlatformUrls property={property} compact={false} showTitle={true} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Paginación profesional */}
      {filteredProperties.length > 0 && (
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            {/* Información de la página */}
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Mostrando{' '}
                <span className="font-medium">
                  {Math.min((currentPage - 1) * itemsPerPage + 1, filteredProperties.length)}
                </span>
                {' '}a{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, filteredProperties.length)}
                </span>
                {' '}de{' '}
                <span className="font-medium">{filteredProperties.length}</span>
                {' '}propiedades
              </span>
              
              {/* Selector de elementos por página */}
              <div className="flex items-center space-x-2">
                <label htmlFor="itemsPerPage" className="text-sm text-gray-700">
                  Por página:
                </label>
                <select
                  id="itemsPerPage"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset a la primera página
                  }}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Controles de navegación */}
            <div className="flex items-center space-x-2">
              {/* Botón Primera página */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              {/* Botón Página anterior */}
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Números de página */}
              {(() => {
                const totalPages = Math.ceil(filteredProperties.length / itemsPerPage);
                const maxVisiblePages = 7;
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                const pageNumbers = [];
                
                // Primera página si no está visible
                if (startPage > 1) {
                  pageNumbers.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className="px-3 py-1 rounded text-gray-700 hover:bg-gray-100"
                    >
                      1
                    </button>
                  );
                  if (startPage > 2) {
                    pageNumbers.push(
                      <span key="ellipsis1" className="px-2 text-gray-400">...</span>
                    );
                  }
                }

                // Páginas visibles
                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded ${
                        currentPage === i
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {i}
                    </button>
                  );
                }

                // Última página si no está visible
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pageNumbers.push(
                      <span key="ellipsis2" className="px-2 text-gray-400">...</span>
                    );
                  }
                  pageNumbers.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-3 py-1 rounded text-gray-700 hover:bg-gray-100"
                    >
                      {totalPages}
                    </button>
                  );
                }

                return pageNumbers;
              })()}

              {/* Botón Página siguiente */}
              <button
                onClick={() => {
                  const totalPages = Math.ceil(filteredProperties.length / itemsPerPage);
                  setCurrentPage(Math.min(totalPages, currentPage + 1));
                }}
                disabled={currentPage >= Math.ceil(filteredProperties.length / itemsPerPage)}
                className={`px-2 py-1 rounded ${
                  currentPage >= Math.ceil(filteredProperties.length / itemsPerPage)
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Botón Última página */}
              <button
                onClick={() => {
                  const totalPages = Math.ceil(filteredProperties.length / itemsPerPage);
                  setCurrentPage(totalPages);
                }}
                disabled={currentPage >= Math.ceil(filteredProperties.length / itemsPerPage)}
                className={`px-2 py-1 rounded ${
                  currentPage >= Math.ceil(filteredProperties.length / itemsPerPage)
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredProperties.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🏠</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hay propiedades
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterType || filterTransaction 
              ? 'No se encontraron propiedades con los filtros aplicados'
              : 'Aún no hay propiedades registradas'
            }
          </p>
          {canEdit && !searchTerm && !filterType && !filterTransaction && (
            <button
              onClick={() => onPropertyEdit?.(null)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Agregar primera propiedad
            </button>
          )}
        </div>
      )}

      {/* Resumen de estadísticas */}
      {filteredProperties.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">📊 Resumen de Resultados</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{quickStats.total}</div>
              <div className="text-sm text-gray-600">Total propiedades</div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="text-2xl font-bold text-green-600">{quickStats.disponibles}</div>
              <div className="text-sm text-gray-600">Disponibles</div>
            </div>
            {quickStats.promedioArriendo > 0 && (
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-lg font-bold text-purple-600">${quickStats.promedioArriendo.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Promedio arriendo</div>
              </div>
            )}
            {quickStats.promedioVenta > 0 && (
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-lg font-bold text-orange-600">${quickStats.promedioVenta.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Promedio venta</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyList;